import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { aiActivityQuery, aiCommandOutput, aiOpenConversation, type AiActivityRow } from '../api/assistant'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'

/**
 * Direct Commands - `Assistant::activity` / `views/assistant/activity.php`:
 * tot ce a rulat agentul, numit si la un loc, cu filtrul pe fel si fereastra
 * de zile. Increderea nu se cere, se arata.
 */
export function ActivityPage({ days, kind, deviceId }: { days: number; kind: string; deviceId: number }) {
  const page = useQuery(aiActivityQuery(days, kind, deviceId))
  const [output, setOutput] = useState<Record<number, string>>({})

  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" style={{ margin: 16 }}>
          {(page.error as Error).message}
        </div>
      </Content>
    )
  }
  if (!page.data)
    return (
      <Content>
        <Spinner />
      </Content>
    )
  const data = page.data
  const link = (d: number, k: string) =>
    `/app/assistant/activity?days=${d}${k ? `&kind=${encodeURIComponent(k)}` : ''}${deviceId ? `&device_id=${deviceId}` : ''}`
  const kindLabel = (k: string) => data.kinds[k]?.[0] ?? k
  const kindIcon = (k: string) => data.kinds[k]?.[2] ?? 'fa-list'

  const showOutput = async (row: AiActivityRow) => {
    if (output[row.source_id] !== undefined) {
      setOutput((cur) => {
        const next = { ...cur }
        delete next[row.source_id]
        return next
      })
      return
    }
    const out = await aiCommandOutput(row.source_id)
    setOutput((cur) => ({
      ...cur,
      [row.source_id]: out.ok ? `${out.state}\n${out.output}` : (out.error ?? ''),
    }))
  }

  return (
    <Content>
      <div id="content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ai-activity-counts">
              {Object.keys(data.kinds).map((k) => (
                <a key={k} className={`ai-activity-count${data.filters.kind === k ? ' is-active' : ''}`} href={link(days, k)}>
                  <i className={`fas ${kindIcon(k)}`} aria-hidden="true"></i>
                  <span className="ai-activity-count-n">{data.counts[k] ?? 0}</span>
                  <span className="ai-activity-count-label">{kindLabel(k)}</span>
                </a>
              ))}
              <a className={`ai-activity-count${data.filters.kind === '' || !data.kinds[data.filters.kind] ? ' is-active' : ''}`} href={link(days, '')}>
                <i className="fas fa-list" aria-hidden="true"></i>
                <span className="ai-activity-count-label">All</span>
              </a>
            </div>
            <div className="ai-activity-range">
              Last{' '}
              {([1, 7, 30, 90] as const).map((d) => (
                <a key={d} href={link(d, kind)} className={days === d ? 'is-active' : ''}>
                  {d === 1 ? '24h' : `${d} days`}
                </a>
              ))}
            </div>
            <div className="panel panel-default">
              <div className="panel-body">
                {data.rows.length === 0 ? (
                  <p className="text-muted" style={{ margin: 0 }}>
                    Nothing in this window. The agent did nothing {kind ? 'of the selected kind ' : ''}in the last {days} days.
                  </p>
                ) : (
                  <table className="table table-hover ai-activity-table">
                    <thead>
                      <tr>
                        <th style={{ width: 150 }}>When</th>
                        <th style={{ width: 180 }}>Machine</th>
                        <th style={{ width: 130 }}>Kind</th>
                        <th>What it did</th>
                        <th style={{ width: 150 }}>Asked by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((row) => (
                        <tr key={`${row.kind}-${row.id}`} className={`ai-sev-${row.severity}`}>
                          <td className="text-muted">{row.at}</td>
                          <td>
                            {row.device_id > 0 ? (
                              <a href={`/app/inventoree/device/${row.device_id}`}>{row.device_name || `#${row.device_id}`}</a>
                            ) : (
                              <span className="text-muted">&mdash;</span>
                            )}
                          </td>
                          <td>
                            <i className={`fas ${kindIcon(row.kind)}`} aria-hidden="true"></i> {kindLabel(row.kind)}
                          </td>
                          <td>
                            {/* Mesajul e deja escapat de cine l-a scris (Activity::feed). */}
                            <span dangerouslySetInnerHTML={{ __html: row.message }} />{' '}
                            {row.conversation_id > 0 ? (
                              <a
                                href="#"
                                className="ai-open-session"
                                onClick={(e) => {
                                  e.preventDefault()
                                  void aiOpenConversation(row.conversation_id).then((o) =>
                                    toast(o.ok ? 'Opened in the agent drawer.' : (o.error ?? ''), 'Adam Intop', o.ok ? 'success' : 'error'),
                                  )
                                }}
                              >
                                open
                              </a>
                            ) : row.source_table === 'it_comenzi' && row.source_id > 0 ? (
                              <>
                                <a
                                  href="#"
                                  className="ai-open-output"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    void showOutput(row)
                                  }}
                                >
                                  output
                                </a>
                                <pre className="ai-activity-output" hidden={output[row.source_id] === undefined}>
                                  {output[row.source_id]}
                                </pre>
                              </>
                            ) : null}
                          </td>
                          <td>{row.who !== '' ? row.who : <span className="text-muted">the agent, on its own</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
